import { ChevronDown, Home } from "lucide-react";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import bellSrc from "@/assets/icons/bell.svg";
import shareSrc from "@/assets/icons/share.svg";
import { ReferralModal } from "@/components/home/ReferralModal";
import { LogoutConfirmationDialog } from "@/components/profile/LogoutConfirmationDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePublicPrimaryConfiguration } from "@/hooks/useConfig";
import { useLoyaltyProfile } from "@/hooks/useLoyaltyProfile";
import { useUnreadNotificationCount } from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";
import { customerTokenService } from "@/services/customer-auth";
import { useCustomerAuthStore } from "@/stores/customerAuthStore";
import { LANGUAGES } from "@/components/common/language";

interface HeaderProps {
  containerClass?: string;
}

export const Header = memo(({ containerClass }: HeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { logout } = useCustomerAuthStore();
  const { data: publicConfig, isLoading: isBrandLoading } =
    usePublicPrimaryConfiguration();

  const resolveAssetUrl = (url?: string | null): string | null => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;

    const apiBase = import.meta.env.VITE_API_BASE_URL || "/api";
    try {
      const base = new URL(apiBase, window.location.origin);
      return `${base.origin}${url.startsWith("/") ? "" : "/"}${url}`;
    } catch {
      return url;
    }
  };

  const brandName = publicConfig?.brand_name?.trim() || "";
  const brandLogo = resolveAssetUrl(publicConfig?.logo_url);

  const isLoginPage = location.pathname === "/login";

  const currentLang =
    LANGUAGES.find((l) => i18n.language.startsWith(l.value)) || LANGUAGES[0];

  const handleLanguageChange = (langValue: string) => {
    i18n.changeLanguage(langValue);
  };

  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);

  const isLoggedIn = !!customerTokenService.getType();
  const { data: unreadCount = 0 } = useUnreadNotificationCount(isLoggedIn);
  const { data: profile } = useLoyaltyProfile(isLoggedIn);
  const referralUrl = profile?.referralCode
    ? `${window.location.origin}/signup/${profile.referralCode}`
    : "";
  const badgeLabel =
    unreadCount >= 100 ? "99+" : unreadCount > 0 ? String(unreadCount) : null;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <header className="relative z-10 h-[72px] flex items-center bg-transparent">
      <div
        className={cn(
          "flex items-center w-full h-full px-4 sm:px-6 justify-between",
          containerClass,
        )}
      >
        <div
          className={cn(
            "flex items-center",
            isLoginPage ? "flex-1 justify-start" : "justify-start",
          )}
        >
          {isLoginPage ? (
            <button
              onClick={() => navigate("/home")}
              className="flex items-center justify-center size-10 rounded-xl border border-black/[0.06] bg-white shadow-[0px_2px_8px_rgba(0,0,0,0.04)] hover:bg-gray-50 transition-all"
            >
              <Home className="w-5 h-5"></Home>
            </button>
          ) : (
            <div
              className="flex items-center gap-2 min-w-0 h-12 cursor-pointer"
              onClick={() => navigate("/home")}
            >
              <div className="w-[160px] h-[40px] shrink-0">
                {isBrandLoading ? (
                  <div className="w-[160px] h-[40px] rounded-md bg-gray-200 animate-pulse" />
                ) : brandLogo ? (
                  <img
                    src={brandLogo}
                    alt={brandName || "Brand logo"}
                    className="w-full h-full object-contain"
                  />
                ) : null}
              </div>
            </div>
          )}
        </div>

        {isLoginPage && (
          <div className="flex-1 flex justify-center">
            <div
              className="flex items-center gap-2 min-w-0 h-12 cursor-pointer"
              onClick={() => navigate("/home")}
            >
              <div className="w-[160px] h-[40px] shrink-0">
                {isBrandLoading ? (
                  <div className="w-[160px] h-[40px] rounded-md bg-gray-200 animate-pulse" />
                ) : brandLogo ? (
                  <img
                    src={brandLogo}
                    alt={brandName || "Brand logo"}
                    className="w-full h-full object-contain"
                  />
                ) : null}
              </div>
            </div>
          </div>
        )}

        <div
          className={cn(
            "flex items-center gap-2",
            isLoginPage ? "flex-1 justify-end" : "justify-end",
          )}
        >
          {isLoginPage ? (
            <div className="size-10 invisible" />
          ) : (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className="flex items-center gap-1.5 px-2 py-2 rounded-lg border border-black/[0.06] bg-white cursor-pointer shadow-sm hover:bg-gray-50 transition-all group outline-none">
                    <img
                      src={currentLang.flag}
                      className="size-4 rounded-full object-cover shadow-sm"
                      alt="flag"
                    />
                    <span className="text-body-1 font-medium text-textMedEm">
                      {currentLang.code}
                    </span>
                    <ChevronDown className="size-4 text-textHighEm group-data-[state=open]:rotate-180 transition-transform" />
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-[120px] p-1 bg-white/80 backdrop-blur-md border border-black/[0.06]"
                >
                  {LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.value)}
                      className="flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded-md hover:bg-black/5"
                    >
                      <img
                        src={lang.flag}
                        className="size-4 rounded-full object-cover"
                        alt={lang.code}
                      />
                      <span className="text-body-1 font-medium text-textMedEm">
                        {lang.label}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <button
                onClick={() => {
                  if (isLoggedIn) {
                    setIsReferralModalOpen(true);
                  } else {
                    navigate("/login");
                  }
                }}
                className="flex items-center justify-center size-10 rounded-xl border border-black/[0.06] bg-white shadow-sm hover:bg-gray-50 transition-all"
              >
                <img src={shareSrc} alt="Share" />
              </button>
              <button
                onClick={() => navigate("/notifications")}
                className="relative flex items-center justify-center size-10 rounded-xl border border-black/[0.06] bg-white shadow-sm hover:bg-gray-50 transition-all"
              >
                <img src={bellSrc} alt="Bell" />
                {badgeLabel && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[#ef4444] text-white text-[10px] font-bold rounded-full leading-none">
                    {badgeLabel}
                  </span>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      <LogoutConfirmationDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
        onConfirm={handleLogout}
      />
      <ReferralModal
        open={isReferralModalOpen}
        onOpenChange={setIsReferralModalOpen}
        referralCode={referralUrl}
      />
    </header>
  );
});

Header.displayName = "Header";
